import { IPublicClientApplication, AccountInfo } from '@azure/msal-browser';
import { AuthTokens } from '@/types/auth.types';

export class MsalTokenService {
  /**
   * Initialize MSAL with tokens from device flow
   * This allows MSAL to handle token refresh using the refresh token
   */
  static initializeWithDeviceFlowTokens(
    instance: IPublicClientApplication,
    tokens: AuthTokens
  ): AccountInfo | null {
    if (!tokens.idToken) {
      console.warn('No ID token available for MSAL initialization');
      return null;
    }

    try {
      // Decode the ID token to extract account information
      const parts = tokens.idToken.split('.');
      if (parts.length !== 3) {
        console.error('Invalid ID token format');
        return null;
      }

      const decoded = JSON.parse(atob(parts[1]));
      
      // Create account info from the ID token claims
      const account: AccountInfo = {
        homeAccountId: `${decoded.oid || decoded.sub}.${decoded.tid}`,
        environment: decoded.iss?.includes('login.microsoftonline.com') 
          ? 'login.microsoftonline.com' 
          : 'login.windows.net',
        tenantId: decoded.tid,
        username: decoded.preferred_username || decoded.email || decoded.upn || '',
        localAccountId: decoded.oid || decoded.sub || '',
        name: decoded.name,
        idTokenClaims: decoded,
        idToken: tokens.idToken,
        nativeAccountId: undefined
      };

      // Set the active account in MSAL
      instance.setActiveAccount(account);

      // Store tokens in MSAL cache format
      // Note: MSAL will use the refresh token to get new access tokens automatically
      if (tokens.refreshToken) {
        // MSAL handles refresh tokens internally through its cache
        // The setActiveAccount above enables silent token refresh
        console.log('MSAL initialized with device flow tokens - refresh token available');
      }

      return account;
    } catch (error) {
      console.error('Failed to initialize MSAL with device flow tokens:', error);
      return null;
    }
  }

  /**
   * Get a fresh access token using MSAL's silent token acquisition
   * This will use the refresh token if the access token is expired
   */
  static async getAccessToken(
    instance: IPublicClientApplication,
    scopes: string[] = ['api://6ef233b2-6c49-4272-a072-5bc6ae2855e8/access_as_user']
  ): Promise<string | null> {
    const account = instance.getActiveAccount();
    
    if (!account) {
      console.warn('No active account in MSAL');
      return null;
    }

    try {
      // Try to get token silently (will use refresh token if needed)
      const response = await instance.acquireTokenSilent({
        scopes,
        account,
        forceRefresh: false
      });

      console.log('Token acquired silently from MSAL');
      return response.accessToken;
    } catch (error) {
      console.error('Failed to acquire token silently:', error);
      
      // If silent acquisition fails, try interactive
      try {
        const response = await instance.acquireTokenPopup({
          scopes,
          account
        });
        
        console.log('Token acquired interactively from MSAL');
        return response.accessToken;
      } catch (popupError) {
        console.error('Failed to acquire token interactively:', popupError);
        return null;
      }
    }
  }

  /**
   * Check if stored tokens are still valid
   */
  static isTokenExpired(expiresIn: number, issuedAt?: number): boolean {
    const now = Math.floor(Date.now() / 1000);
    const tokenIssuedAt = issuedAt || (now - expiresIn);
    const expiresAt = tokenIssuedAt + expiresIn;
    
    // Consider token expired if it expires in less than 5 minutes
    return (expiresAt - now) < 300;
  }
}